import type { Dish, Order, RiskOrder } from '../../models';
import apiManage from '../../request';
import {
    applyHistoryOrderDetailAmountFallback,
    mergeOrderDetailSummaryWithTableRow,
    parseDataAnalysisOrderDetailWithHead,
    type DataAnalysisOrderDetailWithHeadResponse,
} from '../../request/api/data-analysis';
import type { HttpSuccessOrErrorType } from '../../request/http';

/**
 * 判断风控关联订单是否可打开公共订单详情弹窗。
 * @param order - 事件内关联订单行
 * @param isInstant - 即时 Tab 用 order_head_id；云端历史用 server_id
 */
export function canOpenRiskAssociatedOrderDetail(
    order: RiskOrder | null | undefined,
    isInstant = true
): boolean {
    if (!order) {
        return false;
    }
    if (isInstant) {
        const id = order.order_head_id ?? order.id;
        if (id == null) {
            return false;
        }
        const n = Number(id);
        return !Number.isNaN(n) && n > 0;
    }
    const serverId = order.server_id;
    return serverId != null && String(serverId).trim().length > 0;
}

/**
 * 将风控列表/事件内订单行映射为 OrderDetailDialog 摘要占位。
 * @param risk - 风控订单摘要
 */
export function mapRiskOrderToOrderDetailFallback(risk: RiskOrder): Order {
    const row: Order = {};
    if (risk.order_head_id != null) {
        row.order_head_id = risk.order_head_id;
    }
    if (risk.server_id != null && String(risk.server_id).trim() !== '') {
        row.server_id = String(risk.server_id);
    }
    row.table_name = risk.table_name ?? null;
    if (risk.people_count != null) {
        row.customer_num = risk.people_count;
    }
    if (risk.dish_count != null) {
        row.dish_count = risk.dish_count;
    }
    if (risk.avg_dish_per_person != null) {
        row.avg_dish_per_person = risk.avg_dish_per_person;
    }
    if (risk.operator_name) {
        row.open_employee_name = risk.operator_name;
    }
    if (risk.open_time) {
        row.order_start_time = new Date(risk.open_time);
    }
    if (risk.origin_amount != null) {
        row.total_amount = risk.origin_amount;
    }
    if (risk.actual_amount != null) {
        row.actual_amount = risk.actual_amount;
        row.paid_amount = risk.actual_amount;
    }
    if (risk.discount_amount != null) {
        row.discount_total = risk.discount_amount;
    }
    if (risk.refund_amount != null) {
        row.refund_dish_total = risk.refund_amount;
    }
    return row;
}

/**
 * 拉取关联订单的 order_detail_with_head / history_order/head 详情。
 * @param stringId - 餐厅 string_id
 * @param order - 关联订单行
 * @param isInstant - 是否即时风控（order_head_id）
 */
export async function fetchRiskAssociatedOrderDetail(
    stringId: string,
    order: RiskOrder,
    isInstant: boolean
): Promise<{ summary: Order; dishes: Dish[]; hasError: boolean }> {
    const tableFallback = mapRiskOrderToOrderDetailFallback(order);
    try {
        const res = isInstant
            ? await apiManage.get_data_analysis_order_details({
                  string_id: stringId,
                  order_head_id: `${order.order_head_id ?? order.id ?? ''}`,
                  sort: 'ASC',
              })
            : await apiManage.get_data_analysis_res_history_order_details({
                  string_id: stringId,
                  id: `${order.server_id ?? ''}`,
              });
        const hasError =
            res != null &&
            typeof res === 'object' &&
            'error' in res &&
            Boolean((res as { error?: boolean }).error);
        const { order: apiOrder, details } = parseDataAnalysisOrderDetailWithHead(
            res as HttpSuccessOrErrorType<DataAnalysisOrderDetailWithHeadResponse>
        );
        if (!hasError) {
            const normalized = isInstant ? apiOrder : applyHistoryOrderDetailAmountFallback(apiOrder);
            return {
                summary: mergeOrderDetailSummaryWithTableRow(tableFallback, normalized),
                dishes: details ?? [],
                hasError: false,
            };
        }
        return { summary: tableFallback, dishes: [], hasError: true };
    } catch {
        return { summary: tableFallback, dishes: [], hasError: true };
    }
}
